import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';

import uniqueId from 'lodash.uniqueid';
import {RESPONSE_NOTE_LIFETIME} from 'src/contexts/background-task-context/background-task-context-provider';
import {
    getMcpCreditAlertSettings,
    saveMcpCreditAlertSettings,
} from 'src/openapi/admin/apis/McpAlertSettingsApi';
import {getMcpContracts} from 'src/openapi/admin/apis/McpContractsApi';
import {runUserSearch} from 'src/openapi/admin/apis/UserSearchApi';
import {SortDirection} from 'src/openapi/admin/models/SortDirection';
import {UserSortField} from 'src/openapi/admin/models/UserSortField';
import {useAppDispatch} from 'src/store/hooks';

import {Icons} from '@pb/kit-component-icon';
import Loading from '@pb/kit-component-loading';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';
import {ConcurrentStrategies} from '@pb/middleware-api';

import {
    selectMcpAlertError,
    selectMcpAlertSaving,
    selectMcpAlertSettings,
    selectMcpContracts,
    selectMcpContractsLoading,
} from '../../services/mcp-slice';

import {AlertsView} from './alerts-view';

import type {McpCreditAlertSettings} from 'src/openapi/admin/models/McpCreditAlertSettings';
import type {UserSearchCriteria} from 'src/openapi/admin/models/UserSearchCriteria';
import type {UserSearchResult} from 'src/openapi/admin/models/UserSearchResult';

const SEARCH_DEBOUNCE_MS = 300;

const buildUserSearchCriteria = (
    accountId: number,
    query: string,
): UserSearchCriteria => {
    if (query.includes('@')) {
        return {accountIds: [accountId], userEmails: [query]};
    }
    const parts = query.split(/\s+/);
    return {
        accountIds: [accountId],
        firstName: parts[0],
        ...(parts[1] ? {lastName: parts[1]} : {}),
    };
};

type Props = {
    accountId: number,
};

export const AlertsController = ({accountId}: Props) => {
    const apiDispatch = useAppDispatch();
    const {onCreateNote} = usePushNotification();

    const alertSettings = useSelector(selectMcpAlertSettings);
    const alertSaving = useSelector(selectMcpAlertSaving);
    const alertError = useSelector(selectMcpAlertError);
    const contracts = useSelector(selectMcpContracts);
    const contractsLoading = useSelector(selectMcpContractsLoading);

    const [alertUsers, setAlertUsers] = useState<Array<UserSearchResult> | null>(null);
    const initialUsersRef = useRef<Array<UserSearchResult>>([]);
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        apiDispatch(getMcpCreditAlertSettings(
            {accountId},
            {concurrentStrategy: ConcurrentStrategies.LAST},
        ));
        apiDispatch(runUserSearch({
            userSearchRequest: {
                searchCriteria: {accountIds: [accountId]},
                userSortCriteria: {
                    columnId: UserSortField.EMAIL,
                    direction: SortDirection.ASC,
                },
                pagination: {currentPage: 1, pageSize: 250},
            },
        })).then(response => {
            if (response.payload.success) {
                initialUsersRef.current = response.payload.data.result;
                setAlertUsers(response.payload.data.result);
            }
        });
        if (!contracts) {
            apiDispatch(getMcpContracts({accountId}));
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (alertError) {
            onCreateNote({
                id: uniqueId('mcp-alert-error-'),
                status: Status.ERROR,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: RESPONSE_NOTE_LIFETIME,
                body: () => alertError.message || 'Service is temporarily unavailable',
            });
        }
    }, [alertError, onCreateNote]);

    const onSearchUsers = useCallback(
        (query: string) => {
            if (searchTimerRef.current) {
                clearTimeout(searchTimerRef.current);
            }
            if (!query.trim()) {
                setAlertUsers(initialUsersRef.current);
                return;
            }
            searchTimerRef.current = setTimeout(() => {
                apiDispatch(runUserSearch({
                    userSearchRequest: {
                        searchCriteria: buildUserSearchCriteria(accountId, query.trim()),
                        userSortCriteria: {
                            columnId: UserSortField.EMAIL,
                            direction: SortDirection.ASC,
                        },
                        pagination: {currentPage: 1, pageSize: 50},
                    },
                })).then(response => {
                    if (response.payload.success) {
                        setAlertUsers(response.payload.data.result);
                    }
                });
            }, SEARCH_DEBOUNCE_MS);
        },
        [accountId, apiDispatch],
    );

    const onUpdateAlerts = useCallback(
        async (settings: McpCreditAlertSettings): Promise<boolean> => {
            const response = await apiDispatch(
                saveMcpCreditAlertSettings({
                    accountId,
                    mcpSaveCreditAlertSettingsRequest: settings,
                }),
            );

            if (response.payload.success) {
                onCreateNote({
                    id: uniqueId('mcp-alert-save-'),
                    status: Status.SUCCESS,
                    buttonProps: {iconProps: {type: Icons.CLOSE}},
                    autoHideTimeout: RESPONSE_NOTE_LIFETIME,
                    body: () => 'Alert settings have been saved',
                });
                await apiDispatch(
                    getMcpCreditAlertSettings({accountId}),
                );
                return true;
            } else {
                onCreateNote({
                    id: uniqueId('mcp-alert-save-error-'),
                    status: Status.ERROR,
                    buttonProps: {iconProps: {type: Icons.CLOSE}},
                    autoHideTimeout: RESPONSE_NOTE_LIFETIME,
                    body: () => response.payload.data?.message || 'Failed to save alert settings',
                });
                return false;
            }
        },
        [accountId, apiDispatch, onCreateNote],
    );

    const isLoading = !alertSettings
        || contractsLoading
        || !alertUsers
        || !contracts;

    if (isLoading) {
        return <Loading/>;
    }

    return (
        <AlertsView
            accountId={accountId}
            data={alertSettings}
            users={alertUsers}
            contracts={contracts}
            alertsBusy={alertSaving}
            onUpdateAlerts={onUpdateAlerts}
            onSearchUsers={onSearchUsers}
        />
    );
};
